import { Component } from 'preact';
import PropTypes from 'prop-types';
import IMask from 'imask';
import { phoneMasks } from '%/utils/masks';
import getUTM from '%/utils/utm';
import getGADParams from '%/utils/gadTracking';
import InputCpf from '../../../InputCpf';
import validationFactory from '../../../../utils/validation';
import CustomCheck from '../../../CustomCheck';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { ErrorModal } from '../../components/ErrorModal';

export class PersonalInfo extends Component {
  constructor(props) {
    super(props);

    this.state = {
      name: '',
      email: '',
      phone: '',
      cpf: '',
      contactPreferences: [],
      dataPermissions: false,
      contactPreferencesError: false,
      sendingConversion: false,
      showConfirmationModal: false,
      showErrorModal: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.toggleContactOption = this.toggleContactOption.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.sendConversion = this.sendConversion.bind(this);
  }

  componentDidMount() {
    const phoneMask = IMask(this.phoneInput, {
      mask: [...phoneMasks],
    });
    phoneMask.on('accept', () =>
      this.setState(prev => ({
        ...prev,
        phone: phoneMask.value,
      })),
    );
    this.validator = validationFactory('#test-drive-personal-info-form');
  }

  handleChange(field, value) {
    this.setState(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  handleSubmit(e) {
    e.preventDefault();
    const { setTestDriveData } = this.props;
    const { name, email, phone, cpf } = this.state;

    setTestDriveData({ name, email, phone, cpf });

    const formIsValid = this.validateForm();

    if (formIsValid) {
      this.setState({ showConfirmationModal: true });
    }
  }

  getConversionParams() {
    return {
      bait: 'TEST DRIVE',
      utmz: getUTM(),
      gad_tracking: getGADParams(),
      brand: this.props.conversionBrand,
      channel: this.props.channelConversion,
      category: this.props.conversionCategory,
      product_id: this.props.modelId,
      product: `${this.props.modelName} ${this.props.versionName}`,
      version: this.props.versionSlug,
      unit: this.props.unitSlug,
      test_drive_date: this.props.preferredDate,
      test_drive_time: this.props.preferredTime,
      name: this.state.name,
      email: this.state.email,
      phone: this.state.phone,
      cpf: this.state.cpf,
      phoning: this.state.contactPreferences.includes('phoning'),
      mailing: this.state.contactPreferences.includes('mailing'),
      whatsapping: this.state.contactPreferences.includes('whatsapping'),
      data_permissions: this.state.dataPermissions,
    };
  }

  sendConversion() {
    const params = this.getConversionParams();

    this.setState({ sendingConversion: true });

    window.serviceConversion
      .convert(params)
      .then(() => {
        this.setState({
          sendingConversion: false,
          showConfirmationModal: false,
        });
        this.props.nextStep();
      })
      .catch(error => {
        this.setState({
          sendingConversion: false,
          showConfirmationModal: false,
          showErrorModal: true,
        });
        console.error(error);
      });
  }

  validateForm() {
    const invalidInputs = this.validator.validateAll(this.form);
    const formIsValid = invalidInputs.length === 0;

    return formIsValid;
  }

  toggleContactOption(option) {
    const { contactPreferences } = this.state;
    const isSelected = contactPreferences.includes(option);
    const updatedPreferences = isSelected
      ? contactPreferences.filter(item => item !== option)
      : [...contactPreferences, option];

    this.setState(prev => ({
      ...prev,
      contactPreferences: updatedPreferences,
      contactPreferencesError: updatedPreferences.length === 0,
    }));
  }

  render() {
    const {
      name,
      email,
      phone,
      cpf,
      contactPreferences,
      dataPermissions,
      contactPreferencesError,
    } = this.state;

    const { showDataPermissions, dataPermissionsCustomText } = this.props;

    const formIsFilled =
      name.length &&
      email.length &&
      phone.length &&
      contactPreferences.length > 0;

    return (
      <form
        id="test-drive-personal-info-form"
        onSubmit={this.handleSubmit}
        ref={formNode => {
          this.form = formNode;
        }}
      >
        <header className="titles-container">
          <h2 className="test-drive__title">Preencha seus dados</h2>
          <p className="test-drive__subtitle">
            Informe seus dados para receber a confirmação do seu test drive
          </p>
        </header>

        <section className="personal-info-grid">
          <div>
            <label className="test-drive__label">Nome</label>
            <input
              type="text"
              name="name"
              placeholder="Ex.: Ayrton Senna"
              value={name}
              onInput={e => this.handleChange('name', e.target.value)}
              className="test-drive__input"
              data-bouncer-target="#invalid-name"
              required
            />
            <div id="invalid-name" className="invalid-feedback" />
          </div>

          <div>
            <label className="test-drive__label">E-mail</label>
            <input
              type="email"
              name="email"
              placeholder="Ex.: ayrtonsenna@email.com"
              value={email}
              onInput={e => this.handleChange('email', e.target.value)}
              className="test-drive__input"
              data-bouncer-target="#invalid-email"
              required
            />
            <div id="invalid-email" className="invalid-feedback" />
          </div>

          <div>
            <label className="test-drive__label">Telefone</label>
            <input
              type="phone"
              name="phone"
              placeholder="Ex.: (99) 99999-9999"
              value={phone}
              ref={phoneInput => {
                this.phoneInput = phoneInput;
              }}
              className="test-drive__input"
              data-bouncer-target="#invalid-phone"
              data-should-validate="true"
              required
            />
            <div id="invalid-phone" className="invalid-feedback" />
          </div>

          <div>
            <label className="test-drive__label">CPF</label>
            <div className="test-drive__input-cpf-container">
              <InputCpf
                value={cpf}
                placeholder="Ex.: 999.999.999-99"
                className="test-drive__input"
                handleChange={e => this.handleChange('cpf', e.target.value)}
                shouldValidate
                data-bouncer-target="#invalid-cpf"
                required
              />
            </div>
          </div>
        </section>

        <section className="contact-preferences">
          <p className="test-drive__label">Quero receber contato por:</p>
          <div className="contact-options">
            <CustomCheck
              name="mailing"
              value="true"
              onChangeCheckable={() => this.toggleContactOption('mailing')}
              isChecked={contactPreferences.includes('mailing')}
              type="checkbox"
            >
              E-mail
            </CustomCheck>
            <CustomCheck
              name="phoning"
              value="true"
              onChangeCheckable={() => this.toggleContactOption('phoning')}
              isChecked={contactPreferences.includes('phoning')}
              type="checkbox"
            >
              Telefone
            </CustomCheck>
            <CustomCheck
              name="whatsapping"
              value="true"
              onChangeCheckable={() => this.toggleContactOption('whatsapping')}
              isChecked={contactPreferences.includes('whatsapping')}
              type="checkbox"
            >
              Whatsapp
            </CustomCheck>
          </div>
          {contactPreferencesError && (
            <div
              id="invalid-contact-preferences"
              className="invalid-feedback d-block"
            >
              <div className="error-message">
                Selecione pelo menos uma forma de contato.
              </div>
            </div>
          )}
        </section>

        {showDataPermissions ? (
          <CustomCheck
            name="data_permissions"
            value="true"
            onChangeCheckable={e =>
              this.handleChange('dataPermissions', !dataPermissions)
            }
            isChecked={dataPermissions}
            type="checkbox"
          >
            {dataPermissionsCustomText ||
              'Autorizo o uso de minhas informações pessoais para campanhas de marketing.'}
          </CustomCheck>
        ) : null}

        <p className="test-drive__text--small">
          Ao informar meus dados, eu concordo com a{' '}
          <a
            href={this.props.privacyPolicyLink}
            target="_blank"
            rel="noreferrer"
          >
            Política de privacidade
          </a>
          .
        </p>

        <section className="test-drive-buttons">
          <button
            type="button"
            className="test-drive__button test-drive__button--next"
            disabled={!formIsFilled}
            onClick={this.handleSubmit}
          >
            Enviar
          </button>
        </section>

        {this.state.showConfirmationModal && (
          <ConfirmationModal
            onClose={() => this.setState({ showConfirmationModal: false })}
            onConfirm={() => {
              this.sendConversion();
            }}
            loading={this.state.sendingConversion}
          />
        )}
        {this.state.showErrorModal && (
          <ErrorModal
            onClose={() => this.setState({ showErrorModal: false })}
          />
        )}
      </form>
    );
  }
}

PersonalInfo.propTypes = {
  conversionBrand: PropTypes.string,
  channelConversion: PropTypes.string,
  conversionCategory: PropTypes.string,
  modelId: PropTypes.string,
  modelName: PropTypes.string,
  versionName: PropTypes.string,
  versionSlug: PropTypes.string,
  unitSlug: PropTypes.string,
  preferredDate: PropTypes.string,
  preferredTime: PropTypes.string,
  nextStep: PropTypes.func,
  setTestDriveData: PropTypes.func,
  privacyPolicyLink: PropTypes.string,
};
